/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getTheme, DefaultButton, Panel, PanelType, PrimaryButton, Link, IconButton, Spinner, SpinnerSize } from '@fluentui/react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { DoubleListBox, DoubleListBoxContextProvider } from '@bingads-webui-react/double-list-box-fluent';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';
import { helpbuttonStyle, convertToLocalStyle } from '@bingads-webui-campaign-react/common-styles';
import { campaignBrandListAssociationFromProp, getCampaignBrandListAssociationFromProp, getViewModelBulkUpsertErrors } from '@bingads-webui-campaign-react/brand-lists-data';
import { DataSource } from './double-list-box-data-source';

const { palette } = getTheme();
const getStyles = () => ({
  ApplyBrandListPanel: {
    '& .double-list-box > div': {
      height: 'auto',
      '& .ms-FocusZone, & .items': {
        width: '100%',
      },
      '& .bulk-control': {
        padding: 12,
        fontWeight: 600,
      },
      '& table': {
        '& tbody tr': {
          '&:hover': {
            backgroundColor: '#EDEBE9',
          },
        },
        '& td, & th': {
          textAlign: 'left',
          padding: '0 12px',
          '& .helpbutton': {
            verticalAlign: 'text-top',
          },
          '&:last-child': {
            textAlign: 'right',
          },
        },
        '& button': {
          width: 30,
        },
      },
    },
    '& .selected-count': {
      display: 'flex',
      gap: '10px',
    },
    '& .selected-items': {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap',
    },
    '& .error-message': {
      color: palette.redDark,
      marginTop: 12,
    },
    ...convertToLocalStyle(helpbuttonStyle({ palette })),
  },
  PanelFooter: {
    display: 'flex',
    gap: '8px',
  },
});

export const ApplyBrandListPanel = ({
  className, isOpen, type, onSave, onDismiss, brandLists,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);

  const serverContext = useContext(ServerContext);
  const edmProps = {
    cid: serverContext.CurrentCustomer.Id,
    aid: serverContext.CurrentAccount.Id,
  };
  const campaignBrandListAssociation = campaignBrandListAssociationFromProp(edmProps);
  const getCampaignBrandListAssociation = getCampaignBrandListAssociationFromProp(edmProps);

  const [originalAssociation, setOriginalAssociation] = useState(null);
  const [submiting, setSubmiting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const onSelectionChange = useCallback((items) => {
    setSelectedItems(items);
  }, []);
  const dlbRef = React.useRef();
  const [error, setError] = useState(null);

  const applyAssociation = useCallback(async () => {
    setSubmiting(true);
    try {
      const res = await campaignBrandListAssociation({
        brandList: brandLists[0].Id,
        campaigns: selectedItems.map(v => v.id),
        originalAssociation: (originalAssociation || []).map(v => v.id),
        isExclusion: type === 'exclusion',
      });
      const originalAssociationMap = (originalAssociation || []).reduce((acc, v) => {
        acc[v.id] = v;
        return acc;
      }, {});
      let errResult = null;
      res.forEach((v) => {
        const errCode = v.errors.length > 0 ? v.errors[0].Error : null;
        const shouldFilterErrorCodes = [
          'CampaignServiceSharedEntityAssociationDoesNotExist',
          'CampaignServiceSharedListIdInvalid',
          'CampaignServiceFutureFeatureCode',
        ];
        if (!errCode || shouldFilterErrorCodes.includes(errCode)) {
          const association = v.entity.BrandListsAssociations;
          if (association.Status === 'Deleted') {
            delete originalAssociationMap[association.EntityId];
          } else {
            originalAssociationMap[association.EntityId] = {
              id: association.EntityId,
              name: '',
            };
          }
        } else if (!errResult) {
          errResult = v;
        }
      });
      if (!errResult) {
        onSave();
      } else {
        const serverErrors = getViewModelBulkUpsertErrors(i18n, errResult);
        setError(serverErrors[0].message);
        setOriginalAssociation(Object.values(originalAssociationMap));
      }
    } catch (e) {
      const serverErrors = getViewModelBulkUpsertErrors(i18n, e);
      setError(serverErrors[0].message);
    } finally {
      setSubmiting(false);
    }
  }, [brandLists, campaignBrandListAssociation, i18n, onSave, originalAssociation, selectedItems, type]);

  useEffect(() => {
    if (isOpen && brandLists.length > 0) {
      setOriginalAssociation(null);
      setSelectedItems([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandLists, isOpen]);

  const getOriginalAssociation = useCallback(async () => {
    if (originalAssociation !== null) {
      return;
    }
    const res = await getCampaignBrandListAssociation(brandLists[0].Id, type === 'exclusion');
    const data = res.value.map(v => ({
      id: v.EntityId,
      name: v.EntityName,
    }));
    setOriginalAssociation(data);
    if (dlbRef.current) {
      dlbRef.current.setSelectedItems(data);
    }
  }, [brandLists, getCampaignBrandListAssociation, originalAssociation, type]);

  const removeSelect = useCallback((item) => {
    const newSelectedItems = _.filter(selectedItems, selectedItem => selectedItem.id !== item.id);
    setSelectedItems(newSelectedItems);
    dlbRef.current.setSelectedItems(newSelectedItems);
  }, [selectedItems]);

  const onRenderFooterContent = useCallback(() => (
    <div className={classes.PanelFooter}>
      <PrimaryButton className="save-button" onClick={applyAssociation} disabled={submiting}>
        {submiting ? <Spinner size={SpinnerSize.small} /> : i18n.getString(_TL_('Save'))}
      </PrimaryButton>
      <DefaultButton className="cancel-button" onClick={onDismiss}>
        {i18n.getString(_TL_('Cancel'))}
      </DefaultButton>
    </div>
  ), [applyAssociation, classes.PanelFooter, i18n, onDismiss, submiting]);


  const customRenderItems = (items, toggleSelection) => {
    if (!items || items.length === 0) {
      return null;
    }
    const typeMap = {
      protection: i18n.getString(_TL_('Search')),
      exclusion: i18n.getString(_TL_('Performance Max')),
    };
    return (
      <table>
        <thead>
          <tr>
            <th>
              <span>{i18n.getString(_TL_('Campaign name'))}</span>
              <HelpIcon topic="pop_BA_BrandLists_CampaignName" disabled />
            </th>
            <th>{i18n.getString(_TL_('Campaign type'))}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items && items.map(item => (
            <tr key={item.id}>
              <td>{item.name}</td>
              <td>{typeMap[type]}</td>
              <td>
                <IconButton
                  iconProps={{ iconName: item.isSelected ? 'Remove' : 'Add' }}
                  title="Toggle select"
                  ariaLabel="Toggle select"
                  onClick={() => (item.isEditDisabled ? _.noop : toggleSelection(item))}
                />
              </td>
            </tr>
            ))}
        </tbody>
      </table>
    );
  };

  const customRenderBulkControl = (items, onBulkOperation, isSelectedList, innerClasses) => {
    if (isSelectedList) {
      return null;
    }
    const disabled = _.every(items, { isSelected: true });
    return (
      <div className={classNames(innerClasses.bulkControl, 'bulk-control')} >
        <div className="description">
          Campaigns: {totalCampaigns}
        </div>
        <Link onClick={onBulkOperation} disabled={disabled}>Select all</Link>
      </div>
    );
  };

  return (
    <Panel
      className={classNames(className, classes.ApplyBrandListPanel, 'apply-brand-list-panel')}
      headerText={type === 'protection' ? i18n.getString(_TL_('Apply brand protection')) : i18n.getString(_TL_('Apply brand exclusion'))}
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      type={PanelType.medium}
      isOpen={isOpen}
      onDismiss={onDismiss}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      layerProps={{
        styles: { root: { zIndex: 1099 } },
      }}
      onOuterClick={_.noop}
    >
      <div>
        <p>
          {type === 'protection' ? i18n.getString(_TL_('You can only select Broad only campaigns to apply brand protection. Brands that are under review cannot have brand protection applied. ')) : i18n.getString(_TL_('Brand exclusions are only available for Performance Max campaigns. Please note that brands pending editorial review cannot have brand exclusion applied. '))}
          <a className="HelpTopic" href="#" role="button" aria-label="" query="app54850">{i18n.getString(_TL_('Learn more'))}</a>
        </p>
        <DoubleListBoxContextProvider
          pageSize={10}
          pageNumber={1}
        >
          <DataSource
            type={type}
            brandLists={brandLists}
            onCampaignLoaded={(res) => {
              setTotalCampaigns(res['@odata.count']);
              getOriginalAssociation();
            }}
          />
          <DoubleListBox
            showSelectedItems={false}
            componentRef={dlbRef}
            onSelectionChange={onSelectionChange}
            customRenderItems={customRenderItems}
            customRenderBulkControl={customRenderBulkControl}
            customSearchPlaceHolder={i18n.getString(_TL_('Search by campaign name'))}
          />
        </DoubleListBoxContextProvider>
        {originalAssociation ?
          <>
            <p className="selected-count">
              {i18n.getString(_TL_('{{count}} campaigns'), { count: selectedItems.length })}
              {selectedItems && selectedItems.length > 0 && <Link onClick={() => dlbRef.current.setSelectedItems([])}>Remove all</Link>}
            </p>
            <div className="selected-items">
              {!_.isEmpty(selectedItems) && selectedItems.map((item, index) => (
                <SelectedItem
                  key={item.id}
                  itemText={item.name}
                  itemIndex={index}
                  onDelete={() => removeSelect(item)}
                />
                ))}
            </div>
          </> : <Spinner size={SpinnerSize.large} />}
        {error && <div className="error-message">{error}</div>}
      </div>
    </Panel>
  );
};

ApplyBrandListPanel.propTypes = {
  className: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  brandLists: PropTypes.arrayOf(PropTypes.shape({
    Id: PropTypes.number.isRequired,
  })).isRequired,
  type: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

ApplyBrandListPanel.defaultProps = {
  className: undefined,
};
